import { type FormEvent, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { FeatureDisabledState } from "../../components/trainer/FeatureDisabledState";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { useTenant } from "../../hooks/useTenant";
import { useWorkspaceFeatures } from "../../hooks/useWorkspaceFeatures";
import { clientService } from "../../services/clientService";
import type { ClientFormInput, ClientInviteResult } from "../../types/trainer";

const INITIAL_FORM: ClientFormInput = {
  fullName: "",
  email: "",
  optionalNote: "",
};

export function TrainerClientNewPage() {
  const { currentTenant } = useTenant();
  const { data: features } = useWorkspaceFeatures(currentTenant?.id);
  const [form, setForm] = useState<ClientFormInput>(INITIAL_FORM);
  const [createdInvite, setCreatedInvite] = useState<ClientInviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentTenant) return;
    setSaving(true);
    setActionError(null);
    setCopied(false);
    try {
      const result = await clientService.inviteClient(currentTenant.id, form);
      setCreatedInvite(result);
      setForm(INITIAL_FORM);
    } catch (caughtError) {
      console.error("Unable to create client invite", caughtError);
      setActionError("Unable to create this client invite. Please check the details and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function copyInviteLink() {
    if (!createdInvite) return;
    setActionError(null);
    try {
      await navigator.clipboard.writeText(clientService.getInviteLink(createdInvite.invite_url));
      setCopied(true);
    } catch {
      setActionError("Unable to copy the invite link. Select the link text and copy it manually.");
    }
  }

  return (
    <div>
      {features && !features.client_profiles_enabled ? (
        <FeatureDisabledState title="Client profiles are not enabled." />
      ) : (
      <>
      <AdminPageHeader title="Invite client" description="Send a client account activation link for this workspace." />
      <AdminPanel title="Client invite">
        <p className="mb-5 text-sm leading-6 text-graphite/70">
          The client will receive an invite link and complete onboarding themselves. Automated email sending from onboarding@coachgrid.app is coming next.
        </p>
        {actionError ? <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">{actionError}</div> : null}
        <form className="space-y-4" onSubmit={save}>
          <Input label="Client full name" value={form.fullName} onChange={(event) => setForm({ ...form, fullName: event.target.value })} required />
          <Input label="Client email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
          <label className="block text-sm font-medium text-ink">
            Optional note
            <textarea className="mt-2 min-h-28 w-full rounded-md border border-line bg-field px-3 py-2" value={form.optionalNote} onChange={(event) => setForm({ ...form, optionalNote: event.target.value })} />
          </label>
          <Button fullWidth isLoading={saving} type="submit">Create invite</Button>
        </form>
        {createdInvite ? (
          <div className="mt-5 rounded-md border border-pine/20 bg-mint p-4 text-sm text-pine">
            <p className="font-semibold">
              {createdInvite.status === "existing_pending" ? "Pending invite already exists" : "Invite created"}
            </p>
            <p className="mt-2 text-pine/80">
              Automated email sending is not wired yet. Send this link to the client manually for now.
            </p>
            <div className="mt-3 rounded-md border border-pine/20 bg-white p-3 text-ink">
              <p className="break-all text-sm leading-6">{clientService.getInviteLink(createdInvite.invite_url)}</p>
            </div>
            <Button className="mt-3" variant="secondary" onClick={() => void copyInviteLink()}>
              {copied ? "Copied" : "Copy invite link"}
            </Button>
          </div>
        ) : null}
      </AdminPanel>
      </>
      )}
    </div>
  );
}
